import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Currency,
  DateTime,
  DeleteConfirmation,
  IconButton,
  ListView,
  ListViewActions,
  ListViewMenu,
  ListViewSelection,
  Page,
} from '~/components';
import { BulkActionMenu } from '~/components/bulk-action';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useAuth, useRowSelection } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';

import TitleTooltip from '../../TitleTooltip';
import AddMultipleDialog from './AddMultipleDialog';
import BulkDelete from './BulkDelete';
import RevenueAdjustmentForm from './RevenueAdjustmentForm';
import { revenueTypes } from './revenueTypes';
import SettingsActionsBanner from '../../SettingsActionsBanner';

export default function RevenueAdjustmentLedger({ project }) {
  const auth = useAuth();
  const canEdit = project.permissions.edit && auth.projects.sections.billing;
  const api = useApi();
  const { workspace } = useWorkspace();
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).projectSettings(project.id).revenueAdjustments().get();
    setData(data);
  }, [api, workspace.id, project]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selection = useRowSelection({ rows: data });

  const [dialog, setDialog] = useState(null);
  const toast = useToast();

  const handleSaved = async () => {
    await fetchData();
    setDialog(null);
  };

  const handleShowForm = (revenueAdjustment = null) => {
    setDialog(
      <RevenueAdjustmentForm
        currency={project.currency}
        projectId={project.id}
        revenueAdjustmentId={revenueAdjustment?.id}
        readOnly={!canEdit}
        onSaved={handleSaved}
        onClose={() => setDialog(null)}
      />,
    );
  };

  const handleShowMultipleForm = () => {
    setDialog(
      <AddMultipleDialog
        currency={project.currency}
        project={project}
        onSaved={handleSaved}
        onCancel={() => setDialog(null)}
      />,
    );
  };

  const confirmation = useConfirmation();
  const handleDelete = async (revenueAdjustment) => {
    await confirmation.prompt((resolve) => (
      <DeleteConfirmation
        title="Delete Earned Revenue Adjustment"
        resolve={async (result) => {
          if (result) {
            await api.www
              .workspaces(workspace.id)
              .projectSettings(project.id)
              .revenueAdjustments(revenueAdjustment.id)
              .delete();
            toast.success('Earned Revenue Adjustment deleted successfully.');
            await fetchData();
            selection.deselect(revenueAdjustment.id);
          }
          resolve(result);
        }}>
        Are you sure you want to delete this earned revenue adjustment?
      </DeleteConfirmation>
    ));
  };

  const handleBulkDelete = async () => {
    await confirmation.prompt((resolve) => (
      <BulkDelete
        projectId={project.id}
        ids={selection.ids}
        currency={project.currency}
        onComplete={async () => {
          selection.clear();
          await fetchData();
        }}
        onClose={resolve}
      />
    ));
  };

  if (!data) return <PageLoader />;

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Earned Revenue Adjustments</Page.Title>
        <TitleTooltip message="Manual adjustments to earned revenue. These adjustments can be negative or positive and will not impact invoiced revenue." />

        {canEdit && (
          <Page.Actions>
            <IconButton icon="clone-plus" tooltip="Add Multiple" onClick={handleShowMultipleForm} />

            <IconButton icon="plus" tooltip="Add" onClick={() => handleShowForm(null)} />
          </Page.Actions>
        )}
      </Page.TabHeader>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListViewSelection.Column selection={selection} isVisible={canEdit} disabled={!data.length} />
            <ListView.Column width="8rem">Date</ListView.Column>
            <ListView.Column width="12rem">Revenue Type</ListView.Column>
            <ListView.Column minWidth="20rem">Notes</ListView.Column>
            <ListView.Column width="10rem" align="right">
              Amount
            </ListView.Column>
            <ListViewActions.Column />
          </ListView.Header>

          <ListView.Body>
            {data.map((revenueAdjustment) => (
              <ListView.Row
                data-testid="row"
                key={revenueAdjustment.id}
                onClick={() => handleShowForm(revenueAdjustment)}>
                <ListViewSelection.Cell id={revenueAdjustment.id} selection={selection} />
                <ListView.Cell>
                  <DateTime value={revenueAdjustment.date} />
                </ListView.Cell>
                <ListView.Cell>{revenueTypes[revenueAdjustment.revenueType]}</ListView.Cell>
                <ListView.Cell>{revenueAdjustment.notes}</ListView.Cell>
                <ListView.Cell>
                  <Currency value={revenueAdjustment.amount} currency={project.currency} />
                </ListView.Cell>
                <ListViewActions>
                  {canEdit ? (
                    <>
                      <ListViewActions.Edit onClick={() => handleShowForm(revenueAdjustment)} />
                      <hr />
                      <ListViewMenu>
                        <ListViewMenu.Item onClick={() => handleShowForm(revenueAdjustment)}>Edit</ListViewMenu.Item>
                        <ListViewMenu.DeleteItem onClick={() => handleDelete(revenueAdjustment)}>
                          Delete
                        </ListViewMenu.DeleteItem>
                      </ListViewMenu>
                    </>
                  ) : (
                    <ListViewActions.View onClick={() => handleShowForm(revenueAdjustment)} />
                  )}
                </ListViewActions>
              </ListView.Row>
            ))}

            {data.length > 0 && (
              <ListView.TotalRow>
                <ListView.Cell />
                <ListView.Cell>Total</ListView.Cell>
                <ListView.Cell />
                <ListView.Cell />
                <ListView.Cell>
                  <Currency value={_.sumBy(data, 'amount')} currency={project.currency} />
                </ListView.Cell>
                <ListViewActions.Cell />
              </ListView.TotalRow>
            )}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>

          <ListView.Status total={data.length} label="Earned Revenue Adjustment" />
        </ListView>
      </Page.ListView>

      {dialog}

      {selection.any && (
        <SettingsActionsBanner>
          <BulkActionMenu count={selection.count}>
            {({ setIsOpen }) => (
              <BulkActionMenu.Item
                onClick={() => {
                  setIsOpen(false);
                  handleBulkDelete();
                }}>
                Delete
              </BulkActionMenu.Item>
            )}
          </BulkActionMenu>
        </SettingsActionsBanner>
      )}
    </>
  );
}
